import { MutableRefObject } from 'react';
import {
    FieldPath,
    FieldPathValue,
    FieldValues,
    UseControllerProps,
    useController,
} from 'react-hook-form';
import { useFormError } from 'components/form/UseFormError';
import * as SearchableSelect from 'components/select/searchable-select/SearchableSelect';
import { SearchableSelectProps } from 'components/select/searchable-select/SearchableSelect';
import { SelectItem } from 'components/select/types';
import { useFormResetKey } from 'customHooks/useFormResetKey';

interface UseFormSearchableSelectProps<
    T extends FieldValues,
    K extends FieldPath<T>,
    P,
    Item extends FieldPathValue<T, K> & SelectItem<P>,
> extends UseControllerProps<T, K>,
        Omit<SearchableSelectProps<P, Item>, 'onSelectItem' | 'name'> {
    inputRef?: MutableRefObject<HTMLInputElement | null>;
    onSelectItem?: (
        selectedItem: BaseTypeFromArray<Item> | null,
        selectedItems: BaseTypeFromArray<Item>[],
        totalItems: BaseTypeFromArray<Item>[],
    ) => void;
}

type BaseTypeFromArray<T> = T extends (infer U)[] ? (U extends string ? never : U) : never;

const UseFormSearchableSelect = <
    T extends FieldValues,
    K extends FieldPath<T>,
    P,
    Item extends FieldPathValue<T, K> & SelectItem<P>,
>(
    props: UseFormSearchableSelectProps<T, K, P, Item>,
) => {
    const {
        control,
        name,
        defaultValue,
        rules,
        inputRef,
        onSelectItem,
        inputProps,
        ...restFormSelectProps
    } = props;
    const {
        field: { onChange: onChangeInternal, onBlur: onBlurInternal, value, ref, ...restFormProps },
        fieldState: { isTouched },
    } = useController({
        name,
        control,
        defaultValue,
        rules,
    });
    const error = useFormError(name as FieldPath<T>);
    const key = useFormResetKey(isTouched);

    const onChange: (typeof props)['onSelectItem'] = (selectedItem, selectedItems, totalItems) => {
        onChangeInternal(selectedItems);
        onBlurInternal();

        onSelectItem && onSelectItem(selectedItem, selectedItems, totalItems);
    };

    return (
        <SearchableSelect.Root
            key={key}
            ref={r => {
                if (typeof inputRef?.current !== 'undefined') {
                    inputRef.current = r;
                }

                return ref(r);
            }}
            error={error?.message}
            selectedItems={value}
            inputProps={{
                id: name,
                ...inputProps,
            }}
            onSelectItem={onChange as SearchableSelectProps<P, Item>['onSelectItem']}
            onBlur={onBlurInternal}
            {...restFormSelectProps}
            {...restFormProps}
        />
    );
};

export { UseFormSearchableSelect };
export type { UseFormSearchableSelectProps };
